/**
 * @copyright 2026 Cooltrax Pty Ltd. All rights reserved.
 */

import React, { useCallback, useMemo, useState } from 'react'

import { FiDownload, FiFileText, FiRefreshCw } from 'react-icons/fi'

import type { DropdownOption } from '@/components/ui'
import { Alert, Button, TableSkeleton, Toast } from '@/components/ui'
import { PageTitle, useTitleInset } from '@/components/ui/page-header/AppTitleBar'
import { useCompany, useToast } from '@/hooks'

import { AuditSummary } from './AuditSummary'
import { AuditTable } from './AuditTable'
import { DetailPanel } from './detail/DetailPanel'
import { FilterBar } from './FilterBar'
import type { AuditRecord } from './types'
import { useAuditFilters } from './useAuditFilters'
import { useAuditDetail, useAuditExport, useAuditFacets, useAuditLogs } from './useAuditLogs'

interface AuditLogsViewProps {
  /** Record id from the route (`/audit-logs/:auditId`), if any. */
  openRecordId?: string
  /** `search` replaces the current query string, for callers that change filters while navigating. */
  onOpenRecord: (id: string | null, search?: string) => void
}

export const AuditLogsView: React.FC<AuditLogsViewProps> = ({ openRecordId, onOpenRecord }) => {
  const { companies } = useCompany()
  const titleInset = useTitleInset()
  const { toast, show, showToast, hideToast } = useToast()
  const [exporting, setExporting] = useState(false)

  // Default the company filter to the first parent company in the list (a company with no parent
  // within the loaded set), not the first item in the array, which may be a child.
  const fallbackCompanyIds = useMemo(() => {
    if (companies.length === 0) return []
    const firstParent =
      companies.find((c) => !c.parentId) ??
      companies.find((c) => !companies.some((p) => p.id === c.parentId)) ??
      companies[0]
    return [firstParent.id]
  }, [companies])
  const { filters, setFilters, clearFilters, toSearch, activeCount } = useAuditFilters(fallbackCompanyIds)

  const list = useAuditLogs(filters)
  const facets = useAuditFacets(filters)
  const detail = useAuditDetail(openRecordId)
  const runExport = useAuditExport(filters)

  const { rows, namesUnavailable } = list

  const companyOptions: DropdownOption[] = useMemo(
    () => companies.map((c) => ({ value: String(c.id), label: c.name, fullName: c.fullName ?? c.name })),
    [companies]
  )
  const companyNames = useMemo(() => new Map(companies.map((c) => [c.id, c.name])), [companies])

  const listRecord = useMemo(() => rows.find((r) => r.id === openRecordId) ?? null, [rows, openRecordId])
  // A deep link (`/audit-logs/:auditId`) can name a record that is not in the loaded rows — older than
  // the date window, another company, or past the first page. The fetched detail carries the same
  // header fields as a list row, so the panel opens from it rather than silently not opening.
  const selectedRecord = listRecord ?? (detail.data?.id === openRecordId ? detail.data : null)
  const selectedIndex = useMemo(
    () => (openRecordId ? rows.findIndex((r) => r.id === openRecordId) : -1),
    [rows, openRecordId]
  )

  // The Company column earns its width only when more than one company is in play.
  const showCompany = filters.companyIds.length !== 1

  const openEntityHistory = useCallback(
    (record: AuditRecord) => {
      // One navigation: calling setFilters then onOpenRecord(null) lets the close overwrite the
      // new filter with the query string from before it.
      onOpenRecord(null, toSearch({ entityId: record.entityId }))
    },
    [toSearch, onOpenRecord]
  )

  const handleExport = useCallback(async () => {
    setExporting(true)
    showToast({
      header: 'Preparing your export…',
      body: 'Your CSV will download shortly.',
      position: 'end',
      info: true,
    })
    try {
      await runExport()
    } catch {
      // The server refuses an export above its row cap; that and any other failure surface here
      // rather than leaving the user waiting for a file that will never arrive.
      // Toast has no error variant; warning is the strongest it offers.
      showToast({
        header: 'Export failed',
        body: 'Narrow your filters and try again.',
        position: 'end',
        warning: true,
      })
    } finally {
      setExporting(false)
    }
  }, [runExport, showToast])

  const isInitialLoading = list.isLoading
  const isRefetching = list.isFetching && !list.isFetchingNextPage && !isInitialLoading

  const actions = (
    <div className="flex items-center gap-2">
      <Button
        variation="ghost"
        size="sm"
        icon={<FiRefreshCw className={list.isFetching ? 'animate-spin' : ''} />}
        handleClick={() => {
          void list.refetch()
          void facets.refetch()
        }}
      >
        Refresh
      </Button>
      <Button
        variation="outline"
        size="sm"
        icon={<FiDownload />}
        isDisabled={exporting || (facets.data?.total ?? 0) === 0}
        handleClick={() => void handleExport()}
      >
        {exporting ? 'Exporting…' : 'Export CSV'}
      </Button>
    </div>
  )

  const noCompany = filters.companyIds.length === 0

  return (
    <div className="flex h-full min-h-0 flex-col p-4">
      {/* Region A: page header */}
      <div
        className={`relative z-20 shrink-0 rounded-box bg-primary py-1 pr-4 ${titleInset} text-primary-content shadow-md`}
      >
        <div className="flex items-center justify-between gap-4 min-h-8">
          <div className="flex items-center gap-2 min-w-0">
            <FiFileText className="text-lg shrink-0" />
            <div className="flex items-baseline gap-3 min-w-0">
              <PageTitle>Audit Logs</PageTitle>
              <p className="text-sm text-primary-content/70 truncate">Track who changed what, and when</p>
            </div>
          </div>
          {actions}
        </div>
      </div>

      {/* Region B: filter bar.
          Above the table's sticky header (z-10), or an open filter menu is painted underneath it. */}
      <div className="relative z-30 mt-4 shrink-0">
        <FilterBar
          filters={filters}
          companyOptions={companyOptions}
          facets={facets.data}
          isSearching={isRefetching && !!filters.search}
          activeCount={activeCount}
          onChange={setFilters}
          onClear={clearFilters}
        />
      </div>

      {/* Region C: result context line */}
      <div className="mt-3 shrink-0">
        <AuditSummary
          filters={filters}
          facets={facets.data}
          isLoading={facets.isLoading}
          companyNames={companyNames}
          entityName={selectedRecord?.entityName ?? rows.find((r) => r.entityId === filters.entityId)?.entityName}
          onChange={setFilters}
        />
      </div>

      {/* Enrichment failure must never block the list — the rows are sound, only the names are not. */}
      {namesUnavailable && (
        <div className="mt-2 shrink-0">
          <Alert type="info" message="Some user names couldn’t be loaded." dismissible />
        </div>
      )}

      {/* Region D/E: table + detail panel */}
      <div className="mt-3 flex min-h-0 flex-1 overflow-hidden rounded-box border border-base-300 bg-base-100">
        <div className="flex min-w-0 flex-1 flex-col">
          {noCompany ? (
            <div className="flex flex-1 flex-col items-center justify-center gap-3 p-8 text-center">
              <FiFileText className="text-4xl text-base-content/30" />
              <p className="text-lg font-medium">Please select a company to see the audit log</p>
              <p className="max-w-sm text-sm text-base-content/60">
                Choose one or more companies from the Companies filter above.
              </p>
            </div>
          ) : isInitialLoading ? (
            <div className="flex-1 p-2">
              <TableSkeleton columns={showCompany ? 7 : 6} rows={12} rowHeight={48} />
            </div>
          ) : list.isError ? (
            <div className="flex flex-1 items-center justify-center p-4">
              <Alert
                type="error"
                message="Couldn’t load audit records"
                description={list.error instanceof Error ? list.error.message : 'Please try again.'}
                alertActions={
                  <Button variation="outline" size="sm" handleClick={() => void list.refetch()}>
                    Try again
                  </Button>
                }
              />
            </div>
          ) : rows.length === 0 ? (
            <div className="flex flex-1 flex-col items-center justify-center gap-3 p-8 text-center">
              <FiFileText className="text-4xl text-base-content/30" />
              {activeCount > 0 ? (
                <>
                  <p className="text-lg font-medium">No changes match your filters</p>
                  <p className="max-w-sm text-sm text-base-content/60">
                    Try widening the date range or clearing some filters.
                  </p>
                  <Button variation="primary" size="sm" handleClick={clearFilters}>
                    Clear all filters
                  </Button>
                </>
              ) : (
                <>
                  <p className="text-lg font-medium">No audit records yet</p>
                  <p className="max-w-sm text-sm text-base-content/60">
                    Changes to alert configurations, geofences, setpoints and assets will appear here.
                  </p>
                </>
              )}
            </div>
          ) : (
            <AuditTable
              rows={rows}
              total={facets.data?.total}
              selectedId={openRecordId ?? null}
              showCompany={showCompany}
              hasMore={list.hasNextPage}
              isFetchingNextPage={list.isFetchingNextPage}
              loadMoreError={list.isError && rows.length > 0}
              isRefetching={isRefetching}
              onSelect={(record) => onOpenRecord(record.id)}
              onEntityHistory={(record) => setFilters({ entityId: record.entityId })}
              onLoadMore={() => void list.fetchNextPage()}
            />
          )}
        </div>

        {selectedRecord && (
          <DetailPanel
            record={selectedRecord}
            outsideList={!listRecord}
            detail={detail.data}
            isLoading={detail.isLoading}
            isError={detail.isError}
            onRetry={() => void detail.refetch()}
            hasPrev={selectedIndex > 0}
            hasNext={selectedIndex >= 0 && selectedIndex < rows.length - 1}
            onClose={() => onOpenRecord(null)}
            onPrev={() => selectedIndex > 0 && onOpenRecord(rows[selectedIndex - 1].id)}
            onNext={() => selectedIndex < rows.length - 1 && onOpenRecord(rows[selectedIndex + 1].id)}
            onEntityHistory={openEntityHistory}
          />
        )}
      </div>

      <Toast {...toast} show={show} onClose={hideToast} />
    </div>
  )
}
